import Navigo from 'navigo';
import { render } from '@oskecare/fe-utilities/lib/LitElement/index.js';
import { constructPath } from '@oskecare/fe-utilities/scripts/helpers.js';
import { Route } from './models/Route.js';

/**
 * Ecare2 App Router
 * Simple lit-html based SPA router. Based upon the single-file Navigo router.
 *
 * @property {MessageBus} _messageBus
 * @property {CustomerDataManager} _customerDataManager
 * @property {Logger} _logger
 * @property {?string} _basePath
 * @property {function: TemplateResult} _layoutTemplate
 * @property {function: TemplateResult} _notFoundPageTemplate
 * @property {object} _config
 */
export default class Router {
  /**
   * Constructor with dependency injection.
   *
   * @param {MessageBus} messageBus
   * @param {CustomerDataManager} customerDataManager
   * @param {Logger} logger
   * @param {object} config
   */
  constructor({ messageBus, customerDataManager, logger, config }) {
    this._messageBus = messageBus;
    this._customerDataManager = customerDataManager;
    this._logger = logger;
    this._basePath = null;
    this._layoutTemplate = null;
    this._notFoundPageTemplate = null;
    this._config = config;
  }

  /**
   * Router initialization
   *
   * @param {HTMLElement} rootElement - Root Element into which the lit-html templates will be rendered
   * @param {Route[]} routes - Array of route configuration objects
   * @param {Redirect[]} redirects - Array of redirect configuration objects
   * @param {string} basePath - Base URL path
   * @param {boolean} hash - True if hash routing should be used instead of history API (for older browsers)
   * @param {function: TemplateResult} layoutTemplate
   * @param {function: TemplateResult} notFoundPageTemplate
   */
  initialize({
    rootElement,
    routes,
    redirects,
    basePath,
    hash = false,
    layoutTemplate,
    notFoundPageTemplate,
  }) {
    this._layoutTemplate = layoutTemplate;
    this._notFoundPageTemplate = notFoundPageTemplate;

    if (!(rootElement instanceof Element)) {
      throw new Error(`Invalid or missing document root: ${rootElement}`);
    }

    if (!Array.isArray(routes)) {
      throw new Error(`Invalid or missing routes configuration: ${routes}`);
    }

    if (typeof basePath !== 'string') {
      throw new Error(`Invalid or missing base path: ${basePath}`);
    }

    this._basePath = basePath;

    this._navigo = new Navigo(basePath, { hash });
    this._registerRoutes(routes, redirects, rootElement);
    this._navigo.resolve(); // This initializes the Navigo router

    window.navigo = this._navigo;

    // Expose navigate API through event listener
    this._messageBus.subscribe('router', 'navigate', msg => {
      this.navigate(msg.path, msg.absolute, msg.replaceHistory);
    });

    this._messageBus.subscribe('router', 'setQuery', msg => {
      this.setQuery(msg.query);
    });

    this._customerDataManager.attachRouter(this);

    this._attachGlobalLinkHandling(rootElement);
  }

  /**
   * @param {Element} rootElement
   * @private
   */
  _attachGlobalLinkHandling(rootElement) {
    rootElement.addEventListener('osk-link-click', event => {
      if (event.detail.isContextual) {
        const linkWithContext = this._customerDataManager.getLinkWithContext(event.detail.href);
        this.navigate(linkWithContext, false);
      }
      this.navigate(event.detail.href, event.detail.isAbsolute);
    });
  }

  /**
   * @param {string} path
   * @param {boolean} absolute
   * @param {boolean} replaceHistory
   */
  navigate(path, absolute = false, replaceHistory = false) {
    if (absolute === true) {
      this._logger.info(
        `Redirecting to absolute url '${path}'; replaceHistory: ${
          replaceHistory ? 'true' : 'false'
        }`,
        'infra-router',
      );
      if (replaceHistory) {
        window.location.replace(path);
      } else {
        window.location.assign(path);
      }
      return;
    }

    const relativePath = constructPath(path, this._basePath, false);
    this._logger.info(
      `Navigating to path '${relativePath}'; replaceHistory: ${replaceHistory ? 'true' : 'false'}`,
      'infra-router',
    );
    if (replaceHistory) {
      this._navigo.navigate(relativePath, { historyAPIMethod: 'replaceState' });
    } else {
      this._navigo.navigate(relativePath);
    }
  }

  /**
   * Swaps current history entry for 404 page.
   */
  redirectTo404() {
    this.navigate('/404', false, true);
  }

  /**
   * @param {URLSearchParams|string} query
   */
  setQuery(query) {
    const stringQuery = query.toString();
    const path = window.location.pathname.replace(this._basePath, '/');

    this.navigate(`${path}${stringQuery ? `?${stringQuery}` : ''}`);
  }

  /**
   * Publishes a message that is consumed by the `BreadcrumbsBar` widget and updates
   * matching placeholders of the breadcrumbs
   *
   * @see BreadcrumbsBar
   * @param {?object} placeholders
   * @param {?object} query
   */
  setBreadcrumbsData({ placeholders = {}, query = {} }) {
    this._messageBus.publish('router', 'setBreadcrumbsData', { placeholders, query });
  }

  /**
   * Register configured routes in the Navigo router
   *
   * @param {Route[]} routes
   * @param {Redirect[]} redirects
   * @param {HTMLElement} rootElement
   * @private
   */
  _registerRoutes(routes, redirects, rootElement) {
    routes.forEach(route => {
      if (!(route instanceof Route)) {
        throw new Error(`Invalid or missing route declaration: ${route}`);
      }

      if (typeof route.path !== 'string') {
        throw new Error(`Invalid or missing route path: ${route.path}`);
      }

      if (typeof route.template !== 'function') {
        throw new Error(`Invalid or missing route template: ${route.template}`);
      }

      this._navigo.on(
        route.path,
        ({ data, queryString, hashString }) => {
          if (route.isWip && !this._config.allowWipFeatures) {
            this.redirectTo404();
          }
          const query = new URLSearchParams(queryString);
          const querySn = query.get('sn');

          const customerData = this._customerDataManager.getCustomerDataSync();
          const loggedInSn = customerData.loggedInSnData.sn;

          if (querySn === null) {
            this._customerDataManager.changeWorkingSnFromRoute({ workingSn: loggedInSn });
          } else if (
            !route.skipSnQueryVerification &&
            (!customerData.snDataList.some(item => item.sn === querySn) || querySn === loggedInSn)
          ) {
            query.delete('sn');
            let { path } = route;
            if (query.toString()) {
              path += `?${query}`;
            }
            if (hashString) {
              path += `#${hashString}`;
            }
            this.navigate(path, false, true);
            this._customerDataManager.changeWorkingSnFromRoute({ workingSn: loggedInSn });
            return;
          } else {
            this._customerDataManager.changeWorkingSnFromRoute({ workingSn: querySn });
          }

          const page = this._layoutTemplate({
            content: route.template({ pathParams: data, query, anchor: hashString }),
            breadcrumbs: route.breadcrumbs,
            activeMenuItem: route.activeMenuItem,
            heading: route.heading,
            path: route.path,
            shouldRenderSnPicker: route.shouldRenderSnPicker,
            snPickerOnDemandTopic: route.snPickerOnDemandTopic,
            query,
          });
          render(page, rootElement);

          Router.setPageTitle(route.title);
          Router.setPageDescription(route.description);
          this._pushGTMPageView(route);
        },
        {
          before: route.beforeHook,
          after: route.afterHook,
          leave: route.leaveHook,
        },
      );
    });

    redirects.forEach(redirect => {
      this._navigo.on(redirect.path, ({ queryString, hashString }) => {
        let path = redirect.isAbsolute
          ? redirect.redirectUrl
          : constructPath(redirect.redirectUrl, this._basePath);

        if (redirect.keepQuery && queryString) {
          path += `?${queryString}`;
        }
        if (redirect.keepHash && hashString) {
          path += `#${hashString}`;
        }
        this.navigate(path, redirect.isAbsolute, true);
      });
    });

    // Register redirect link by url param
    this._navigo.on('/redirect', ({ params }) => {
      if (params?.url) {
        const isRelative = /^\/[^/]/.test(params.url); // relative url starts with a single /
        this.navigate(params.url, !isRelative, true);
      } else {
        this.navigate('/');
      }
    });

    this._navigo.notFound(() => {
      render(
        this._layoutTemplate({
          content: this._notFoundPageTemplate(),
          breadcrumbs: [],
          activeMenuItem: null,
          heading: null,
          shouldRenderSnPicker: false,
          snPickerOnDemandTopic: null,
          query: new URLSearchParams(),
        }),
        rootElement,
      );
    });
  }

  /**
   * Sets page title.
   * Sets default title if title is not provided.
   *
   * @param {string} title
   */
  static setPageTitle(title) {
    if (typeof title !== 'string') {
      throw new Error(`Invalid route title: ${title}`);
    }

    window.document.title = title;
  }

  /**
   * Sets page description.
   * Sets empty page description if description is not provided.
   *
   * @param {string} description
   */
  static setPageDescription(description) {
    if (typeof description !== 'string') {
      throw new Error(`Invalid route description: ${description}`);
    }

    window.document.querySelector('meta[name="description"]').setAttribute('content', description);
  }

  /**
   * Reset dataLayer and push virtual page view
   */
  _pushGTMPageView(route) {
    if (!route.hasCustomPageViewHandling) {
      this._messageBus.publish('mhub', 'pushPageView', {
        type: route.type,
        section: route.section,
        trail:
          route.breadcrumbs
            ?.slice(1, -1)
            .map(breadcrumb => breadcrumb.name)
            .join('/') || '/',
      });
    }
  }
}
